import React, { useMemo, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { HiDotsHorizontal } from 'react-icons/hi';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import { FaBrain, FaTools } from 'react-icons/fa';

import { Signal, useComputed, useSignalEffect } from '@preact/signals-react';
import { VisuallyLogo } from '@/features/strapi/components/logo';
import { AgentMessageWidgetContextMenu } from '@/features/ai-agents/components/agent-message-widget-context-menu';
import {
  shouldShowContextMenu,
  SystemMessageType,
} from '@/features/ai-agents/components/context-menu';
import { AiChatMessage } from '@/features/ai-agents/shared/models';
import { MarkdownRenderer } from '@/features/ai-agents/chat/markdown-renderer';
import { conditional } from '@/utils/conditional';

export interface AgentMessageProps {
  message?: AiChatMessage;
  type?: SystemMessageType;
  showReasoning?: boolean;
  showTools?: boolean;
  processedEvents?: Signal<string[]>;
  isProcessing?: Signal<boolean>;
}

export function AgentMessage({
  message,
  type,
  showReasoning,
  showTools,
  processedEvents,
  isProcessing,
}: AgentMessageProps) {
  const contentRef = useRef(null);
  const [isContextMenuOpen, setIsContextMenuOpen] = React.useState(false);
  const isContextMenuShown = React.useMemo(
    () => shouldShowContextMenu(type),
    [type],
  );

  const [isReasoningCollapsed, setIsReasoningCollapsed] = React.useState(true);
  const [isToolsCollapsed, setIsToolsCollapsed] = React.useState(true);

  const formattedDate = useMemo(() => {
    if (!message) {
      return `Now`;
    }
    const date = new Date(message.created_at * 1000);
    const time = date.toLocaleTimeString([], {
      hour: `2-digit`,
      minute: `2-digit`,
      hour12: false,
    });

    const month = date.toLocaleString(`en-US`, { month: `short` });
    const day = date.getDate().toString().padStart(2, `0`);
    const year = date.getFullYear();

    return `${time} on ${month} ${day}, ${year}`;
  }, [message]);

  const eventMessage = useComputed(() => {
    if (processedEvents) {
      const value = processedEvents?.value;
      if (value.length > 0) {
        return value[value.length - 1];
      }
    }
    return ``;
  });

  // eslint-disable-next-line consistent-return
  useSignalEffect(() => {
    const handler = (ev: Event) => {
      ev.preventDefault();
      ev.stopPropagation();
      ev?.stopImmediatePropagation();
      const href = (ev.target as HTMLElement)?.getAttribute(`href`);
      if (href) {
        window.postMessage(href);
      }
    };
    if (contentRef.current && !isProcessing?.value) {
      try {
        document
          ?.querySelectorAll(`a[href*='#edit-']`)
          ?.forEach((elem) => elem?.addEventListener(`click`, handler));

        /* eslint-disable-next-line no-empty */
      } catch (_) {}

      return () => {
        try {
          document
            ?.querySelectorAll(`a[href*='#edit-']`)
            ?.forEach((elem) => elem.removeEventListener(`click`, handler));
          /* eslint-disable-next-line no-empty */
        } catch (_) {}
      };
    }
  });

  return (
    <Wrapper>
      <Header>
        <HeaderSender>
          <VisuallyLogo small />
          <HeaderName>Visually</HeaderName>
          <HeaderDate>{formattedDate}</HeaderDate>
        </HeaderSender>
        {isContextMenuShown && (
          <Popover
            onClickOutside={() => setIsContextMenuOpen((prev) => !prev)}
            positions={[`bottom`, `left`, `top`, `right`]}
            isOpen={isContextMenuOpen}
            containerStyle={{ zIndex: `10000` }}
            content={({ position, childRect, popoverRect }) => (
              <ArrowContainer
                position={position}
                childRect={childRect}
                popoverRect={popoverRect}
                arrowColor="white"
                arrowClassName="change-target-arrow"
                arrowSize={10}
              >
                {type === SystemMessageType.WIDGET && (
                  <AgentMessageWidgetContextMenu />
                )}
              </ArrowContainer>
            )}
          >
            <ContextMenuButton
              onClick={() => setIsContextMenuOpen((prev) => !prev)}
            >
              <HiDotsHorizontal size={20} />
            </ContextMenuButton>
          </Popover>
        )}
      </Header>
      <Content
        className={isProcessing?.value ? `processing` : ``}
        ref={contentRef}
      >
        {showReasoning && message?.extra_data?.reasoning_steps?.length > 0 && (
          <CollapseSection
            collasped={isReasoningCollapsed}
            onClick={() => setIsReasoningCollapsed((prev) => !prev)}
          >
            <div className="collapse-title">
              <FaBrain />
              Reasoning
            </div>
            <div
              className="collapse-list"
              style={{ display: isReasoningCollapsed ? `none` : `flex` }}
            >
              {message?.extra_data?.reasoning_steps?.map((step) => (
                <div className="collapse-row" key={step.title + step.reasoning}>
                  <div className="collapse-header">{step?.title}</div>
                  <div className="collapse-content">{step?.reasoning}</div>
                </div>
              ))}
            </div>
          </CollapseSection>
        )}

        {showTools && message?.tool_calls?.length > 0 && (
          <CollapseSection
            collasped={isToolsCollapsed}
            onClick={() => setIsToolsCollapsed((prev) => !prev)}
          >
            <div className="collapse-title">
              <FaTools />
              Tool Calls
            </div>
            <div
              className="collapse-list"
              style={{ display: isToolsCollapsed ? `none` : `flex` }}
            >
              {message?.tool_calls?.map((tool) => (
                <div className="collapse-row" key={tool.tool_call_id}>
                  <div className="collapse-header">{tool?.tool_name}</div>
                  <div className="collapse-content">
                    {tool?.tool_args?.[`title`]}
                  </div>
                </div>
              ))}
            </div>
          </CollapseSection>
        )}

        {eventMessage?.value && (
          <EventWrapper>
            <FaBrain className="icon" color="#858d94" size={25} />
            <EventContent>{eventMessage}</EventContent>
          </EventWrapper>
        )}

        {conditional(
          <TypingIndicator />,
          !!message?.streamingError,
          <div style={{ color: `red` }}>{message?.content}</div>,
          !!message?.content,
          <MarkdownRenderer>{message?.content}</MarkdownRenderer>,
        )}
      </Content>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Header = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 0.5rem;

  font-family: Eesti, sans-serif;
  font-size: 1.4rem;

  .gatsby-image-wrapper {
    width: 2rem;
    height: 2rem;
  }
`;

const HeaderSender = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
`;

const HeaderName = styled.span`
  color: black;
`;

const HeaderDate = styled.span`
  color: #858d94;
`;

const EventWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 2rem;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;

  .icon {
    display: none;
    animation: breath 3s ease-in-out infinite;

    @keyframes breath {
      0% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.1);
      }
      100% {
        transform: scale(1);
      }
    }
  }
`;

const EventContent = styled.div`
  width: 100%;
  white-space: pre-line;
  font-family: Eesti, sans-serif;
  font-size: 1.4rem;
  line-height: 1.5;
  font-weight: 600;
  position: relative;
  color: #858d94;

  &&::before {
    content: '';
    position: absolute;
    top: 0;
    left: -150%;
    height: 100%;
    width: 150%;
    background: linear-gradient(
      120deg,
      rgba(255, 255, 255, 0) 30%,
      rgba(255, 255, 255, 0.3) 50%,
      rgba(255, 255, 255, 0) 70%
    );
    animation: shimmer 6s infinite ease-in-out;
    pointer-events: none;
  }

  @keyframes shimmer {
    0% {
      left: -150%;
    }
    100% {
      left: 150%;
    }
  }
`;

const Content = styled.div`
  margin-inline-start: 3.5rem;
  font-family: Eesti, sans-serif;
  font-weight: 400;
  font-size: 1.4rem;
  line-height: 1.5;
  border-radius: 1rem;
  box-shadow: none;
  border: none;
  background-color: transparent;
  max-width: 90%;
  padding: 0.6rem 0.3rem;
  align-self: flex-start;
  overflow: hidden;
  padding-bottom: 5rem;

  pre:has(code) {
    background-color: #ffffff;
    padding: 2rem;
    overflow: scroll;
    border-radius: 1.5rem;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.1);
    color: black;
    white-space: break-spaces;
  }
  /*
  pre:has(code[class='language-json']) {
    max-height: 6rem;
    overflow: hidden;
    color: transparent;
    white-space: nowrap;
    pointer-events: none;
    user-select: none;
    position: relative;

    code:after {
      content: 'Visual Inputs Generated Successfully';
      position: absolute;
      color: black;
      top: 50%;
      left: 2rem;
      transform: translate(0, -50%);
      font-size: 1.4rem;
      display: block;
    }
  }

  &&.processing pre:has(code[class='language-json']) {
    code:after {
      content: 'Generating Visual Inputs...';
    }
  }

  p:has(a[href='#edit-visual']) {
    br {
      display: none;
    }
  }
*/

  a[href='#edit-visual'],
  a[href='#edit-code'],
  a[href*='#edit-strategy'] {
    display: inline-flex;
    flex-direction: column;
    text-decoration: none;
    color: black;
    margin-top: 1.5rem;
    margin-right: 1rem;
    border: 1px solid black;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    transition: background-color 0.3s ease-in-out, color 0.3s ease-in-out;

    &:hover {
      background: black;
      color: white;
    }
  }
`;

const CollapseSection = styled.div`
  color: #858d94;
  cursor: pointer;
  margin-bottom: 1.5rem;

  .collapse-title {
    font-weight: 500;
    text-transform: uppercase;
    font-size: 1.5rem;
    margin-bottom: 1rem;

    display: flex;
    flex-direction: row;
    gap: 1rem;
    align-items: center;
  }

  .collapse-list {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    margin-inline-start: 1rem;
  }

  .collapse-row {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .collapse-header {
    font-weight: 600;
  }

  .collapse-content {
    font-weight: 400;
    margin-inline-start: 0.5rem;
  }
`;

const ContextMenuButton = styled.button`
  opacity: 0.4;
  display: flex;
  justify-self: center;
  align-items: center;
  border-radius: 0.5rem;
  box-shadow: 0 1px 7px 0 rgba(0, 0, 0, 0.09);
  border: solid 1px #979797;
  background-color: #d8d8d8;
  padding: 0rem 1rem;
  color: #858d94;
  cursor: pointer;
  transition: opacity 0.2s ease-in-out;
  &:hover {
    opacity: 0.6;
  }

  &:active {
    opacity: 0.4;
  }
`;

const bouncingAnimation = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-0.3rem);
  }
`;

const TypingDotsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
  justify-content: flex-start;
  padding-top: 0.5rem;
`;

const TypingDot = styled.span`
  background-color: #858d94;
  border-radius: 50%;
  height: 0.5rem;
  width: 0.5rem;

  &:nth-child(1) {
    animation: ${bouncingAnimation} 1s infinite 0s;
  }
  &:nth-child(2) {
    animation: ${bouncingAnimation} 1s infinite 0.2s;
  }
  &:nth-child(3) {
    animation: ${bouncingAnimation} 1s infinite 0.4s;
  }
`;

export const TypingIndicator = () => (
  <TypingDotsContainer>
    <TypingDot />
    <TypingDot />
    <TypingDot />
  </TypingDotsContainer>
);
